import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { API_ENABLED, API_URL, currentLocale } from '../lib/api';

/**
 * Reports each page view to the backend so the admin panel can show visitor
 * analytics. Uses fetch with keepalive so a view still registers when the
 * visitor navigates away immediately, and fails silently.
 */
export default function usePageTracking() {
  const location = useLocation();
  const lastPath = useRef(null);
  const enteredAt = useRef(Date.now());
  const visitId = useRef(null);

  useEffect(() => {
    if (!API_ENABLED) return undefined;

    const path = location.pathname + (location.search || '');

    // React 18 StrictMode mounts twice in development; don't double count.
    if (lastPath.current === path) return undefined;
    lastPath.current = path;

    const previousId = visitId.current;
    const secondsOnPrevious = Math.round((Date.now() - enteredAt.current) / 1000);

    // Close off the previous page view with its duration.
    if (previousId && secondsOnPrevious > 0) {
      fetch(`${API_URL}/track/${previousId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ duration: Math.min(secondsOnPrevious, 86400) }),
        keepalive: true,
      }).catch(() => {});
    }

    enteredAt.current = Date.now();

    fetch(`${API_URL}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        path,
        page_title: document.title,
        referrer: document.referrer || null,
        locale: currentLocale(),
      }),
      keepalive: true,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        visitId.current = json?.data?.id || null;
      })
      .catch(() => {});

    return undefined;
  }, [location.pathname, location.search]);
}
